import { useEffect, useRef, useState } from "react";
import api, { apiError } from "@/lib/api";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { statusTone, TEMPLATE_VARIABLES, resolveImg } from "@/lib/constants";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Save, Send, RotateCw, Trash2, Plus, Upload, BellRing, FileText, ShieldCheck, Mail } from "lucide-react";
import { toast } from "sonner";

const WEBHOOK_ACTIONS = [
  ["new_lead_created", "New lead created"], ["quote_generated", "Quote generated"], ["quote_sent", "Quote sent"],
  ["quote_accepted", "Quote accepted"], ["client_menu_choices_received", "Client drink choices received"],
  ["contract_generated", "Contract generated"], ["contract_sent", "Contract sent"], ["invoice_generated", "Invoice generated"],
  ["invoice_sent", "Invoice sent"], ["deposit_marked_paid", "Deposit marked paid"], ["balance_marked_paid", "Balance marked paid"],
  ["menu_generated", "Menu generated"], ["alcohol_list_generated", "Alcohol list generated"], ["event_confirmed", "Event confirmed"],
  ["final_reminder_needed", "Final reminder needed"], ["event_completed", "Event completed"], ["client_followup_needed", "Client follow-up needed"],
];

const NOTIF_GROUPS = [
  ["lead", "New leads", "When a new quote request / lead comes in"],
  ["quote", "Quotes", "Quote sent, viewed and accepted"],
  ["menu", "Menu & drink choices", "Client drink choices, needs-help, extras, menu confirmed, alcohol & supplies lists"],
  ["contract", "Contracts", "Contract generated, signing link sent, contract signed"],
  ["payment", "Payments", "Invoice / payment link, deposit paid, balance paid"],
  ["reminder", "Final reminders", "When a final pre-event reminder is due"],
  ["completion", "Event completed", "When an event is marked completed"],
  ["system", "System alerts", "Failed client emails and internal errors"],
];

const PHOTO_ROLES = ["hero", "showcase", "gallery", "menu"];
const ROLE_LABELS = { hero: "Hero (top banner)", showcase: "Showcase strip", gallery: "Gallery grid", menu: "Menu preview" };

const PRICING_FIELDS = [
  ["regular_bartender_rate", "Regular bartender rate ($/hr)"], ["promo_bartender_rate", "Promotional rate ($/hr)"],
  ["min_bartender_rate", "Minimum rate ($/hr)"], ["mobile_bar_rental", "Mobile bar rental ($)"],
  ["transportation_default", "Transportation default ($)"], ["supplies_fee", "Supplies fee ($)"],
  ["cocktail_package_fee", "Cocktail package fee ($)"], ["extra_bartender", "Extra bartender ($)"],
  ["extra_hour", "Extra hour ($)"], ["glassware_helper_rate", "Glassware helper ($/hr)"],
  ["glassware_washing_station", "Washing station ($)"], ["glassware_security_deposit", "Glassware security deposit ($)"],
  ["hst_rate", "HST rate (%)"], ["deposit_pct", "Deposit (%)"],
];

export default function Settings() {
  const [pricing, setPricing] = useState(null);
  const [transport, setTransport] = useState(null);
  const [addons, setAddons] = useState(null);
  const [webhooks, setWebhooks] = useState(null);
  const [templates, setTemplates] = useState([]);
  const [logs, setLogs] = useState([]);
  const [gallery, setGallery] = useState([]);
  const [reminders, setReminders] = useState(null);
  const [guide, setGuide] = useState(null);
  const [guideUploading, setGuideUploading] = useState(false);
  const fileRef = useRef();
  const guideRef = useRef();
  const [upCategory, setUpCategory] = useState("Gallery");
  const [upCaption, setUpCaption] = useState("");
  const [upRole, setUpRole] = useState("gallery");
  const [uploading, setUploading] = useState(false);
  const [pw, setPw] = useState({ current_password: "", new_password: "", confirm_password: "" });
  const [pwSaving, setPwSaving] = useState(false);
  const [newTmpl, setNewTmpl] = useState({ name: "", key: "", subject: "", body: "" });
  const [tmplCreateOpen, setTmplCreateOpen] = useState(false);
  const [tmplDelete, setTmplDelete] = useState(null);
  const [emailCfg, setEmailCfg] = useState(null);
  const [resendKey, setResendKey] = useState("");
  const [testTo, setTestTo] = useState("");
  const [notif, setNotif] = useState(null);
  const [notifLogs, setNotifLogs] = useState([]);

  const loadAll = () => {
    api.get("/settings/pricing").then((r) => setPricing(r.data));
    api.get("/settings/transportation").then((r) => setTransport(r.data));
    api.get("/settings/addons").then((r) => setAddons(r.data));
    api.get("/settings/webhooks").then((r) => setWebhooks(r.data));
    api.get("/templates").then((r) => setTemplates(r.data));
    api.get("/webhook-logs").then((r) => setLogs(r.data));
    api.get("/gallery").then((r) => setGallery(r.data));
    api.get("/settings/reminders").then((r) => setReminders(r.data));
    api.get("/selection-guide").then((r) => setGuide(r.data));
    api.get("/email-settings").then((r) => setEmailCfg(r.data));
    api.get("/settings/notifications").then((r) => setNotif(r.data));
    api.get("/notifications").then((r) => setNotifLogs(r.data));
  };
  useEffect(() => { loadAll(); }, []);

  const saveEmail = async () => {
    try {
      const payload = { ...emailCfg };
      delete payload.resend_api_key_set;
      if (resendKey.trim()) payload.resend_api_key = resendKey.trim();
      const { data } = await api.put("/email-settings", payload);
      setEmailCfg(data); setResendKey("");
      toast.success("Email settings saved");
    } catch (e) { toast.error(e.response?.data?.detail || "Could not save email settings"); }
  };
  const clearResendKey = async () => {
    try {
      const { data } = await api.put("/email-settings", { clear_api_key: true });
      setEmailCfg(data); setResendKey("");
      toast.success("Resend API key removed — using managed sender");
    } catch { toast.error("Could not remove the key"); }
  };
  const sendTest = async () => {
    if (!testTo.trim()) { toast.error("Enter a recipient email for the test"); return; }
    try {
      const { data } = await api.post("/email-settings/test", { recipient: testTo.trim() });
      toast.success(`Test email sent via ${data.path} — from ${data.sender}`);
    } catch (e) { toast.error(e.response?.data?.detail || "Test email failed"); }
  };

  const uploadPhoto = async (file) => {
    if (!file) return;
    setUploading(true);
    const fd = new FormData();
    fd.append("file", file);
    fd.append("category", upCategory || "Gallery");
    fd.append("caption", upCaption);
    fd.append("role", upRole || "gallery");
    try {
      await api.post("/gallery/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Photo uploaded"); setUpCaption(""); loadAll();
    } catch (e) { toast.error(apiError(e.response?.data?.detail) || "Upload failed"); }
    finally { setUploading(false); if (fileRef.current) fileRef.current.value = ""; }
  };
  const deletePhoto = async (id) => { await api.delete(`/gallery/${id}`); toast.success("Photo removed"); loadAll(); };
  const updatePhotoRole = async (id, role) => {
    try { await api.patch(`/gallery/${id}`, { role }); toast.success("Placement updated"); loadAll(); }
    catch (e) { toast.error(apiError(e.response?.data?.detail) || "Update failed"); }
  };
  const uploadGuide = async (file) => {
    if (!file) return;
    setGuideUploading(true);
    const fd = new FormData(); fd.append("file", file);
    try {
      const { data } = await api.post("/selection-guide/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setGuide(data); toast.success("Selection guide uploaded");
    } catch (e) { toast.error(apiError(e.response?.data?.detail) || "Upload failed"); }
    finally { setGuideUploading(false); if (guideRef.current) guideRef.current.value = ""; }
  };
  const saveGuide = async (patchObj) => {
    const next = { ...guide, ...patchObj };
    setGuide(next);
    try { await api.put("/selection-guide", next); toast.success("Saved"); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  const save = async (key, body, msg) => {
    try { await api.put(`/settings/${key}`, body); toast.success(msg || "Saved"); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  const changePassword = async () => {
    if (pw.new_password.length < 8) { toast.error("New password must be at least 8 characters long"); return; }
    if (pw.new_password !== pw.confirm_password) { toast.error("New password and confirmation do not match"); return; }
    setPwSaving(true);
    try {
      await api.patch("/auth/password", { current_password: pw.current_password, new_password: pw.new_password });
      toast.success("Password updated successfully");
      setPw({ current_password: "", new_password: "", confirm_password: "" });
    } catch (e) { toast.error(apiError(e.response?.data?.detail) || "Could not update password"); }
    finally { setPwSaving(false); }
  };

  const createTemplate = async () => {
    if (!newTmpl.name.trim()) { toast.error("Template name is required"); return; }
    const key = (newTmpl.key || newTmpl.name).toLowerCase().replace(/[^a-z0-9]+/g, "_").replace(/^_+|_+$/g, "");
    try {
      await api.post("/templates", { ...newTmpl, key });
      toast.success("Template created");
      setNewTmpl({ name: "", key: "", subject: "", body: "" });
      setTmplCreateOpen(false);
      api.get("/templates").then((r) => setTemplates(r.data));
    } catch (e) { toast.error(apiError(e.response?.data?.detail) || "Could not create template"); }
  };

  const deleteTemplate = async (id) => {
    try {
      await api.delete(`/templates/${id}`);
      toast.success("Template deleted");
      api.get("/templates").then((r) => setTemplates(r.data));
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setTmplDelete(null); }
  };

  return (
    <div className="animate-fade-in">
      <div className="mb-6"><p className="eyebrow mb-2">Configuration</p><h1 className="font-serif text-4xl">Settings</h1></div>

      <Tabs defaultValue="pricing">
        <TabsList className="flex flex-wrap h-auto bg-clay p-1 rounded-xl mb-6">
          <TabsTrigger value="pricing" data-testid="tab-pricing">Pricing</TabsTrigger>
          <TabsTrigger value="transport" data-testid="tab-transport">Transportation</TabsTrigger>
          <TabsTrigger value="addons" data-testid="tab-addons">Add-ons</TabsTrigger>
          <TabsTrigger value="templates" data-testid="tab-templates">Templates</TabsTrigger>
          <TabsTrigger value="notifications" data-testid="tab-notifications">Notifications</TabsTrigger>
          <TabsTrigger value="automation" data-testid="tab-automation">Automation</TabsTrigger>
          <TabsTrigger value="photos" data-testid="tab-photos">Photos</TabsTrigger>
          <TabsTrigger value="documents" data-testid="tab-documents">Documents</TabsTrigger>
          <TabsTrigger value="reminders" data-testid="tab-reminders">Reminders</TabsTrigger>
          <TabsTrigger value="email" data-testid="tab-email">Email</TabsTrigger>
          <TabsTrigger value="account" data-testid="tab-account">Account / Security</TabsTrigger>
        </TabsList>

        {/* PRICING */}
        <TabsContent value="pricing">
          {pricing && (
            <div className="df-card p-6">
              <div className="grid sm:grid-cols-3 gap-4">
                {PRICING_FIELDS.map(([k, label]) => (
                  <div key={k}>
                    <Label className="df-label">{label}</Label>
                    <Input type="number" className="df-input" value={pricing[k] ?? ""} onChange={(e) => setPricing({ ...pricing, [k]: Number(e.target.value) })} data-testid={`pricing-${k}`} />
                  </div>
                ))}
              </div>
              <div className="grid gap-4 mt-6">
                {[["default_quote_terms", "Default quote terms"], ["default_contract_terms", "Default contract terms"], ["default_invoice_notes", "Default invoice notes"], ["alcohol_disclaimer", "Alcohol responsibility note"], ["glassware_policy", "Glassware policy"], ["fire_note", "Fire/flair note"]].map(([k, label]) => (
                  <div key={k}><Label className="df-label">{label}</Label><Textarea className="df-input min-h-[70px]" value={pricing[k] ?? ""} onChange={(e) => setPricing({ ...pricing, [k]: e.target.value })} data-testid={`pricing-${k}`} /></div>
                ))}
              </div>
              <Button onClick={() => save("pricing", pricing)} className="btn-dark mt-6" data-testid="save-pricing"><Save className="h-4 w-4" /> Save Pricing</Button>
            </div>
          )}
        </TabsContent>

        {/* TRANSPORT */}
        <TabsContent value="transport">
          {transport && (
            <div className="df-card p-6">
              <p className="text-sm text-muted-foreground mb-4">Default transportation fee per city. You can always override on each quote.</p>
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
                {transport.cities.map((c, i) => (
                  <div key={c.city} className="flex items-center gap-3">
                    <span className="text-sm flex-1">{c.city}</span>
                    <Input type="number" className="df-input max-w-[110px]" value={c.fee} onChange={(e) => {
                      const cities = [...transport.cities]; cities[i] = { ...c, fee: Number(e.target.value) }; setTransport({ ...transport, cities });
                    }} data-testid={`transport-${c.city}`} />
                  </div>
                ))}
              </div>
              <Button onClick={() => save("transportation", transport)} className="btn-dark mt-6" data-testid="save-transport"><Save className="h-4 w-4" /> Save Fees</Button>
            </div>
          )}
        </TabsContent>

        {/* ADDONS */}
        <TabsContent value="addons">
          {addons && (
            <div className="df-card p-6">
              <div className="space-y-3">
                {addons.items.map((a, i) => (
                  <div key={a.key} className="flex flex-wrap items-center gap-3 border-b border-line pb-3">
                    <Input className="df-input flex-1 min-w-[180px]" value={a.label} onChange={(e) => { const items = [...addons.items]; items[i] = { ...a, label: e.target.value }; setAddons({ ...addons, items }); }} />
                    <Input type="number" className="df-input max-w-[110px]" value={a.price} onChange={(e) => { const items = [...addons.items]; items[i] = { ...a, price: Number(e.target.value) }; setAddons({ ...addons, items }); }} data-testid={`addon-${a.key}`} />
                    <span className="text-xs text-muted-foreground w-20">{a.unit}</span>
                    <button onClick={() => setAddons({ ...addons, items: addons.items.filter((_, j) => j !== i) })} className="text-destructive p-1"><Trash2 className="h-4 w-4" /></button>
                  </div>
                ))}
              </div>
              <button onClick={() => setAddons({ ...addons, items: [...addons.items, { key: "addon_" + Date.now(), label: "New add-on", price: 0, unit: "flat", enabled: true }] })} className="btn-outline text-xs mt-4"><Plus className="h-3 w-3" /> Add</button>
              <Button onClick={() => save("addons", addons)} className="btn-dark mt-6 ml-3" data-testid="save-addons"><Save className="h-4 w-4" /> Save Add-ons</Button>
            </div>
          )}
        </TabsContent>

        {/* TEMPLATES */}
        <TabsContent value="templates">
          <div className="df-card p-6 mb-4 flex flex-wrap items-center justify-between gap-3">
            <p className="text-sm text-muted-foreground flex-1 min-w-[240px]">
              Available variables: {TEMPLATE_VARIABLES.map((v) => <code key={v} className="mx-1 px-1.5 py-0.5 rounded bg-clay text-ink text-xs">{`{${v}}`}</code>)}
            </p>
            <Button onClick={() => setTmplCreateOpen(true)} className="btn-dark shrink-0" data-testid="new-template-button"><Plus className="h-4 w-4" /> New Template</Button>
          </div>
          <div className="space-y-3">
            {templates.map((t) => (
              <details key={t.id} className="df-card p-5" data-testid={`template-item-${t.id}`}>
                <summary className="cursor-pointer font-medium flex items-center justify-between gap-3">
                  <span>{t.name} <span className="text-xs text-muted-foreground">({t.key})</span></span>
                  <button type="button" onClick={(e) => { e.preventDefault(); setTmplDelete(t); }} className="text-destructive hover:bg-destructive/10 rounded-lg p-1.5" title="Delete template" data-testid={`template-delete-${t.id}`}><Trash2 className="h-4 w-4" /></button>
                </summary>
                <div className="mt-4 space-y-3">
                  <div><Label className="df-label">Subject</Label><Input className="df-input" defaultValue={t.subject} onBlur={(e) => api.patch(`/templates/${t.id}`, { subject: e.target.value })} /></div>
                  <div><Label className="df-label">Body</Label><Textarea className="df-input min-h-[140px]" defaultValue={t.body} onBlur={(e) => api.patch(`/templates/${t.id}`, { body: e.target.value })} data-testid={`template-body-${t.key}`} /></div>
                  <p className="text-xs text-muted-foreground">Changes save automatically when you click away.</p>
                </div>
              </details>
            ))}
          </div>

          <Dialog open={tmplCreateOpen} onOpenChange={setTmplCreateOpen}>
            <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
              <DialogHeader><DialogTitle className="font-serif text-2xl">New Email Template</DialogTitle></DialogHeader>
              <div className="space-y-3 pt-1">
                <div><Label className="df-label">Template name *</Label><Input className="df-input" value={newTmpl.name} onChange={(e) => setNewTmpl({ ...newTmpl, name: e.target.value })} placeholder="e.g. Holiday promo" data-testid="new-template-name" /></div>
                <div><Label className="df-label">Key (optional)</Label><Input className="df-input" value={newTmpl.key} onChange={(e) => setNewTmpl({ ...newTmpl, key: e.target.value })} placeholder="Auto-generated from name if blank" data-testid="new-template-key" /></div>
                <div><Label className="df-label">Subject</Label><Input className="df-input" value={newTmpl.subject} onChange={(e) => setNewTmpl({ ...newTmpl, subject: e.target.value })} data-testid="new-template-subject" /></div>
                <div><Label className="df-label">Body</Label><Textarea className="df-input min-h-[160px]" value={newTmpl.body} onChange={(e) => setNewTmpl({ ...newTmpl, body: e.target.value })} data-testid="new-template-body" /></div>
              </div>
              <Button onClick={createTemplate} className="btn-dark w-full mt-2" data-testid="save-template-button"><Save className="h-4 w-4" /> Create Template</Button>
            </DialogContent>
          </Dialog>

          <Dialog open={!!tmplDelete} onOpenChange={(o) => !o && setTmplDelete(null)}>
            <DialogContent className="max-w-sm">
              <DialogHeader><DialogTitle className="font-serif text-2xl">Delete template?</DialogTitle></DialogHeader>
              <p className="text-sm text-muted-foreground">Delete “{tmplDelete?.name}”? This cannot be undone. Sent emails are not affected.</p>
              <div className="flex gap-2 mt-3">
                <Button onClick={() => setTmplDelete(null)} className="btn-outline flex-1" data-testid="cancel-delete-template">Cancel</Button>
                <Button onClick={() => deleteTemplate(tmplDelete.id)} className="flex-1 bg-destructive text-white hover:bg-destructive/90" data-testid="confirm-delete-template">Delete</Button>
              </div>
            </DialogContent>
          </Dialog>
        </TabsContent>

        {/* NOTIFICATIONS */}
        <TabsContent value="notifications">
          {notif && (
            <div className="df-card p-6 mb-6 max-w-2xl">
              <h3 className="font-serif text-2xl mb-1 flex items-center gap-2"><BellRing className="h-5 w-5 text-olive" /> Owner Notifications</h3>
              <p className="text-sm text-muted-foreground mb-5">Get an internal email whenever an important client or event action happens — sent directly from the app, no Make.com needed. Up to 2 recipient addresses.</p>

              <Label className="df-label">Recipient emails (max 2)</Label>
              <div className="grid sm:grid-cols-2 gap-3 mb-5">
                {[0, 1].map((i) => (
                  <Input key={i} type="email" className="df-input" placeholder={i === 0 ? "events@drunkfruit-ca.com" : "second@email.com (optional)"} value={notif.recipients?.[i] || ""} onChange={(e) => { const r = [...(notif.recipients || [])]; r[i] = e.target.value; setNotif({ ...notif, recipients: r }); }} data-testid={`notif-recipient-${i}`} />
                ))}
              </div>

              <Label className="df-label">Admin base URL (for buttons inside notification emails)</Label>
              <Input className="df-input mb-1" placeholder="https://drunkfruit-ca.com" value={notif.admin_base_url || ""} onChange={(e) => setNotif({ ...notif, admin_base_url: e.target.value })} data-testid="notif-base-url" />
              <p className="text-xs text-muted-foreground mb-5">“Open Lead / Open Event” buttons in the emails point here. If you're logged out, the link sends you to /login first, then straight to the record.</p>

              <div className="border-t border-line pt-4">
                <p className="text-sm font-medium mb-3">Notify me about</p>
                <div className="space-y-3">
                  {NOTIF_GROUPS.map(([key, label, desc]) => (
                    <div key={key} className="flex items-start justify-between gap-4 border-b border-line pb-3">
                      <div><div className="text-sm font-medium">{label}</div><div className="text-xs text-muted-foreground">{desc}</div></div>
                      <Switch checked={notif.toggles?.[key] !== false} onCheckedChange={(v) => setNotif({ ...notif, toggles: { ...(notif.toggles || {}), [key]: v } })} data-testid={`notif-toggle-${key}`} />
                    </div>
                  ))}
                </div>
              </div>
              <Button onClick={() => save("notifications", notif, "Notification settings saved")} className="btn-dark mt-6" data-testid="save-notifications"><Save className="h-4 w-4" /> Save Notifications</Button>
            </div>
          )}

          <div className="df-card p-6 max-w-2xl">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-serif text-2xl">Notification Log</h3>
              <button onClick={loadAll} className="btn-outline text-xs" data-testid="notif-log-refresh"><RotateCw className="h-3 w-3" /> Refresh</button>
            </div>
            {notifLogs.length === 0 && <p className="text-sm text-muted-foreground py-6 text-center">No notifications sent yet.</p>}
            <div className="space-y-2">
              {notifLogs.map((l) => (
                <div key={l.id} className="flex flex-wrap items-center justify-between gap-2 text-sm border-b border-line py-2" data-testid={`notif-log-${l.id}`}>
                  <div className="min-w-0">
                    <span className="font-medium">{l.subject || l.action_type}</span>
                    <div className="text-xs text-muted-foreground truncate max-w-md">{(l.recipients || []).join(", ")} · {new Date(l.timestamp).toLocaleString()}{l.error ? ` · ${l.error}` : ""}</div>
                  </div>
                  <Badge variant="outline" className={statusTone(l.status)}>{l.status}</Badge>
                </div>
              ))}
            </div>
          </div>
        </TabsContent>

        {/* AUTOMATION */}
        <TabsContent value="automation">
          {webhooks && (
            <div className="df-card p-6 mb-6">
              <div className="flex flex-wrap items-start justify-between gap-4">
                <div>
                  <h3 className="font-serif text-2xl mb-1">Make.com Webhooks (optional)</h3>
                  <p className="text-sm text-muted-foreground max-w-xl">Drunk Fruit now emails the owner directly for every action (see the Notifications tab). Make.com is <strong>optional</strong> — turn it on only if you also want to POST full JSON payloads to Make.com scenarios.</p>
                </div>
                <label className="flex items-center gap-2 text-sm shrink-0 whitespace-nowrap"><Switch checked={!!webhooks.make_enabled} onCheckedChange={(v) => setWebhooks({ ...webhooks, make_enabled: v })} data-testid="make-enabled-toggle" /> Enable Make.com Webhooks</label>
              </div>
              {webhooks.make_enabled && (
                <div className="space-y-3 mt-5 border-t border-line pt-5">
                  {WEBHOOK_ACTIONS.map(([key, label]) => {
                    const last = webhooks.last_sent?.[key];
                    return (
                      <div key={key} className="flex flex-wrap items-center gap-3">
                        <span className="text-sm w-48">{label}</span>
                        <Input className="df-input flex-1 min-w-[200px]" placeholder="https://hook.make.com/…" value={webhooks.events[key] || ""} onChange={(e) => setWebhooks({ ...webhooks, events: { ...webhooks.events, [key]: e.target.value } })} data-testid={`webhook-${key}`} />
                        {webhooks.events[key] && <Button onClick={() => testHook(webhooks.events[key], key, loadAll)} variant="ghost" className="text-xs h-9"><Send className="h-3 w-3" /> Test</Button>}
                        {last && <Badge variant="outline" className={statusTone(last.status)}>{last.status}</Badge>}
                      </div>
                    );
                  })}
                </div>
              )}
              <Button onClick={() => save("webhooks", webhooks, "Automation settings saved")} className="btn-dark mt-6" data-testid="save-webhooks"><Save className="h-4 w-4" /> Save Automation</Button>
            </div>
          )}

          <div className="df-card p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-serif text-2xl">Webhook Log</h3>
              <button onClick={loadAll} className="btn-outline text-xs"><RotateCw className="h-3 w-3" /> Refresh</button>
            </div>
            {logs.length === 0 && <p className="text-sm text-muted-foreground py-6 text-center">No webhook activity yet.</p>}
            <div className="space-y-2">
              {logs.map((l) => (
                <div key={l.id} className="flex flex-wrap items-center justify-between gap-2 text-sm border-b border-line py-2">
                  <div className="min-w-0"><span className="font-medium">{l.action_type}</span><div className="text-xs text-muted-foreground truncate max-w-md">{l.url || "no url"} · {new Date(l.timestamp).toLocaleString()}</div></div>
                  <div className="flex items-center gap-2">
                    <Badge variant="outline" className={statusTone(l.status)}>{l.status}{l.response_code ? ` · ${l.response_code}` : ""}</Badge>
                    {l.status === "failed" && l.url && <button onClick={() => retryHook(l.id, loadAll)} className="text-xs text-olive" data-testid={`retry-${l.id}`}><RotateCw className="h-3 w-3 inline" /> Retry</button>}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </TabsContent>
        {/* PHOTOS */}
        <TabsContent value="photos">
          <div className="df-card p-6 mb-6">
            <h3 className="font-serif text-2xl mb-1">Photo Manager</h3>
            <p className="text-sm text-muted-foreground mb-4">Upload your own Drunk Fruit event photos — they appear across the public website based on their <strong>placement role</strong>: <em>Hero</em> (top banner), <em>Showcase</em> (feature strip), <em>Gallery</em> (grid) and <em>Menu</em> (menu preview). Stock placeholders are clearly labelled and can be replaced or removed anytime.</p>
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3 items-end">
              <div><Label className="df-label">Category / label</Label><Input className="df-input" value={upCategory} onChange={(e) => setUpCategory(e.target.value)} data-testid="upload-category" /></div>
              <div><Label className="df-label">Caption</Label><Input className="df-input" value={upCaption} onChange={(e) => setUpCaption(e.target.value)} data-testid="upload-caption" /></div>
              <div><Label className="df-label">Placement role</Label>
                <select className="df-input" value={upRole} onChange={(e) => setUpRole(e.target.value)} data-testid="upload-role">
                  {PHOTO_ROLES.map((r) => <option key={r} value={r}>{ROLE_LABELS[r]}</option>)}
                </select>
              </div>
              <div>
                <input ref={fileRef} type="file" accept="image/*" hidden onChange={(e) => uploadPhoto(e.target.files[0])} data-testid="upload-input" />
                <Button onClick={() => fileRef.current?.click()} disabled={uploading} className="btn-dark w-full" data-testid="upload-button"><Upload className="h-4 w-4" /> {uploading ? "Uploading…" : "Upload Photo"}</Button>
              </div>
            </div>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
            {gallery.length === 0 && <p className="text-sm text-muted-foreground col-span-full py-8 text-center">No photos yet.</p>}
            {gallery.map((g) => (
              <div key={g.id} className="df-card overflow-hidden group" data-testid={`gallery-item-${g.id}`}>
                <div className="relative h-40">
                  <img src={resolveImg(g.url)} alt={g.caption} className="h-full w-full object-cover" />
                  <button onClick={() => deletePhoto(g.id)} className="absolute top-2 right-2 bg-white/90 rounded-full p-1.5 opacity-0 group-hover:opacity-100 transition-opacity text-destructive" data-testid={`delete-photo-${g.id}`}><Trash2 className="h-4 w-4" /></button>
                  <span className={`absolute bottom-2 left-2 text-[9px] px-2 py-0.5 rounded-full ${g.source === "stock" ? "bg-white/85 text-ink" : "bg-ink text-white"}`}>{g.source === "stock" ? "Placeholder" : g.source === "uploaded" ? "Uploaded" : "Drunk Fruit"}</span>
                  <span className="absolute top-2 left-2 text-[9px] px-2 py-0.5 rounded-full bg-olive text-white capitalize" data-testid={`photo-role-badge-${g.id}`}>{g.role || "gallery"}</span>
                </div>
                <div className="p-3 space-y-2">
                  <div><div className="text-xs font-medium truncate">{g.category}</div><div className="text-[11px] text-muted-foreground truncate">{g.caption}</div></div>
                  <select className="df-input py-1.5 text-xs" value={g.role || "gallery"} onChange={(e) => updatePhotoRole(g.id, e.target.value)} data-testid={`photo-role-${g.id}`}>
                    {PHOTO_ROLES.map((r) => <option key={r} value={r}>{ROLE_LABELS[r]}</option>)}
                  </select>
                </div>
              </div>
            ))}
          </div>
        </TabsContent>

        {/* DOCUMENTS / SELECTION GUIDE */}
        <TabsContent value="documents">
          <div className="df-card p-6 max-w-2xl">
            <h3 className="font-serif text-2xl mb-1 flex items-center gap-2"><FileText className="h-5 w-5 text-olive" /> Cocktail &amp; Mocktail Selection Guide</h3>
            <p className="text-sm text-muted-foreground mb-5">This standard PDF is offered as a secure download link with every quote email (unless you turn it off), and appears on the client's quote page. Upload or replace it here.</p>
            {guide?.url ? (
              <div className="flex flex-wrap items-center gap-3 bg-clay rounded-xl p-4 mb-5" data-testid="guide-current">
                <FileText className="h-6 w-6 text-olive shrink-0" />
                <div className="flex-1 min-w-0"><div className="text-sm font-medium truncate">{guide.filename || "Selection Guide.pdf"}</div><div className="text-[11px] text-muted-foreground">Uploaded {guide.uploaded_at ? new Date(guide.uploaded_at).toLocaleDateString() : "—"}</div></div>
                <a href={resolveImg("/api/public/selection-guide/file")} target="_blank" rel="noreferrer" className="btn-outline text-xs" data-testid="guide-open">Open PDF</a>
              </div>
            ) : <p className="text-sm text-muted-foreground mb-5">No guide uploaded yet.</p>}
            <input ref={guideRef} type="file" accept="application/pdf" hidden onChange={(e) => uploadGuide(e.target.files[0])} data-testid="guide-upload-input" />
            <div className="flex flex-wrap items-center gap-4">
              <Button onClick={() => guideRef.current?.click()} disabled={guideUploading} className="btn-dark" data-testid="guide-upload-button"><Upload className="h-4 w-4" /> {guideUploading ? "Uploading…" : "Upload / Replace Guide"}</Button>
              <label className="flex items-center gap-2 text-sm"><Switch checked={!!guide?.enabled} onCheckedChange={(v) => saveGuide({ enabled: v })} data-testid="guide-enabled" /> Available with quotes</label>
              <label className="flex items-center gap-2 text-sm"><Switch checked={!!guide?.attach_by_default} onCheckedChange={(v) => saveGuide({ attach_by_default: v })} data-testid="guide-attach-default" /> Attach by default</label>
            </div>
          </div>
        </TabsContent>

        {/* REMINDERS */}
        <TabsContent value="reminders">
          {reminders && (
            <div className="df-card p-6 max-w-xl">
              <h3 className="font-serif text-2xl mb-1 flex items-center gap-2"><BellRing className="h-5 w-5 text-olive" /> Auto Reminders</h3>
              <p className="text-sm text-muted-foreground mb-5">Automatically email each client a final reminder a few days before their confirmed event.</p>
              <div className="flex items-center justify-between mb-5 border-b border-line pb-4">
                <span className="text-sm font-medium">Enable automatic reminders</span>
                <Switch checked={reminders.enabled} onCheckedChange={(v) => setReminders({ ...reminders, enabled: v })} data-testid="reminders-enabled" />
              </div>
              <div className="mb-5"><Label className="df-label">Days before event</Label><Input type="number" min="0" className="df-input max-w-[120px]" value={reminders.days_before} onChange={(e) => setReminders({ ...reminders, days_before: Number(e.target.value) })} data-testid="reminders-days" /></div>
              <div className="mb-6"><Label className="df-label">Reminder template</Label>
                <select className="df-input" value={reminders.template_key} onChange={(e) => setReminders({ ...reminders, template_key: e.target.value })} data-testid="reminders-template">
                  {templates.map((t) => <option key={t.id} value={t.key}>{t.name}</option>)}
                </select>
                <p className="text-xs text-muted-foreground mt-2">Edit the wording of this template in the Templates tab. Reminders run daily and won't send twice for the same event.</p>
              </div>
              <Button onClick={() => save("reminders", reminders, "Reminder settings saved")} className="btn-dark" data-testid="save-reminders"><Save className="h-4 w-4" /> Save Reminders</Button>
            </div>
          )}
        </TabsContent>

        {/* ACCOUNT / SECURITY */}
        <TabsContent value="account">
          <div className="df-card p-6 max-w-xl">
            <h3 className="font-serif text-2xl mb-1 flex items-center gap-2"><ShieldCheck className="h-5 w-5 text-olive" /> Change Password</h3>
            <p className="text-sm text-muted-foreground mb-5">Update the password for your admin account. Enter your current password to confirm it's you. New password must be at least 8 characters.</p>
            <div className="space-y-4">
              <div><Label className="df-label">Current password</Label><Input type="password" autoComplete="current-password" className="df-input" value={pw.current_password} onChange={(e) => setPw({ ...pw, current_password: e.target.value })} data-testid="account-current-password" /></div>
              <div><Label className="df-label">New password</Label><Input type="password" autoComplete="new-password" className="df-input" value={pw.new_password} onChange={(e) => setPw({ ...pw, new_password: e.target.value })} data-testid="account-new-password" /></div>
              <div><Label className="df-label">Confirm new password</Label><Input type="password" autoComplete="new-password" className="df-input" value={pw.confirm_password} onChange={(e) => setPw({ ...pw, confirm_password: e.target.value })} data-testid="account-confirm-password" /></div>
            </div>
            <Button onClick={changePassword} disabled={pwSaving} className="btn-dark mt-6" data-testid="save-password"><ShieldCheck className="h-4 w-4" /> {pwSaving ? "Updating…" : "Update Password"}</Button>
          </div>
        </TabsContent>

        {/* EMAIL BRANDING */}
        <TabsContent value="email">
          {emailCfg && (
            <div className="df-card p-6 max-w-2xl space-y-4">
              <h3 className="font-serif text-2xl mb-1 flex items-center gap-2"><Mail className="h-5 w-5 text-olive" /> Email / Domain Sending</h3>
              <div className="rounded-xl bg-clay/60 border border-line p-4 text-sm text-muted-foreground leading-relaxed" data-testid="email-branding-note">
                <strong className="text-ink">How sending works:</strong> Until your own Resend domain is verified, client emails go out through the managed sender — fully branded as <strong>Drunk Fruit Mobile Bar</strong> with your reply-to address, and no third-party branding in the email body or on client pages. Once you add a Resend API key and set the domain status to <strong>Verified</strong> below, all client emails send truly from <em>{emailCfg.from_email || "events@drunkfruit-ca.com"}</em>. If Resend ever fails, the managed sender is used automatically so email never stops.
                <div className="mt-3 pt-3 border-t border-line">
                  <strong className="text-ink">Connect your own domain (Resend):</strong>
                  <ol className="list-decimal pl-5 mt-1 space-y-0.5">
                    <li>Create a <strong>Resend</strong> account and add the domain <strong>{emailCfg.resend_domain || "drunkfruit-ca.com"}</strong>.</li>
                    <li>Add the <strong>SPF</strong> record Resend gives you (TXT).</li>
                    <li>Add the <strong>DKIM</strong> record(s) Resend gives you (TXT / CNAME).</li>
                    <li>Add a <strong>DMARC</strong> record if needed (TXT, e.g. <code>v=DMARC1; p=none;</code>).</li>
                    <li><strong>Verify the domain</strong> in Resend.</li>
                    <li>Paste your <strong>Resend API key</strong> below and set status to <strong>Verified</strong> — emails then send from <em>events@drunkfruit-ca.com</em>.</li>
                  </ol>
                </div>
              </div>
              <div className="grid sm:grid-cols-2 gap-4">
                <div><Label className="df-label">From name (sender display name)</Label><Input className="df-input" value={emailCfg.from_name || ""} onChange={(e) => setEmailCfg({ ...emailCfg, from_name: e.target.value })} data-testid="email-from-name" /></div>
                <div><Label className="df-label">From email</Label><Input className="df-input" value={emailCfg.from_email || ""} onChange={(e) => setEmailCfg({ ...emailCfg, from_email: e.target.value })} data-testid="email-from-email" /></div>
                <div><Label className="df-label">Reply-to email</Label><Input className="df-input" value={emailCfg.reply_to || ""} onChange={(e) => setEmailCfg({ ...emailCfg, reply_to: e.target.value })} data-testid="email-reply-to" /></div>
                <div><Label className="df-label">Resend domain</Label><Input className="df-input" value={emailCfg.resend_domain || ""} onChange={(e) => setEmailCfg({ ...emailCfg, resend_domain: e.target.value })} placeholder="drunkfruit-ca.com" data-testid="resend-domain" /></div>
              </div>
              <div>
                <Label className="df-label">Resend domain status</Label>
                <select className="df-input" value={emailCfg.resend_status || "not_configured"} onChange={(e) => setEmailCfg({ ...emailCfg, resend_status: e.target.value })} data-testid="resend-status">
                  <option value="not_configured">Not configured</option>
                  <option value="pending">Pending (DNS added, awaiting verification)</option>
                  <option value="verified">Verified — send from my domain</option>
                </select>
                <p className="text-xs text-muted-foreground mt-1">Set to <strong>Verified</strong> only after Resend confirms your domain. While not verified, the managed sender is used.</p>
              </div>
              <div>
                <Label className="df-label flex items-center gap-2">Resend API key {emailCfg.resend_api_key_set && <span className="text-[10px] uppercase tracking-widest text-olive border border-olive/40 rounded-full px-2 py-0.5" data-testid="resend-key-badge">Configured</span>}</Label>
                <Input type="password" className="df-input" value={resendKey} onChange={(e) => setResendKey(e.target.value)} placeholder={emailCfg.resend_api_key_set ? "•••••••• (stored) — paste a new key to replace" : "re_..."} data-testid="resend-api-key" />
                <p className="text-xs text-muted-foreground mt-1">Stored securely in backend secrets only — never shown in the browser or saved to the database.{emailCfg.resend_api_key_set && <> <button onClick={clearResendKey} className="text-destructive underline" data-testid="resend-key-clear">Remove key</button></>}</p>
              </div>
              <div><Label className="df-label">Brand email signature (footer)</Label><Textarea className="df-input" value={emailCfg.signature || ""} onChange={(e) => setEmailCfg({ ...emailCfg, signature: e.target.value })} data-testid="email-signature" /></div>
              <div className="flex flex-wrap items-end gap-3 pt-3 border-t border-line">
                <div className="flex-1 min-w-[200px]"><Label className="df-label">Send a test email to</Label><Input className="df-input" value={testTo} onChange={(e) => setTestTo(e.target.value)} placeholder="you@example.com" data-testid="email-test-to" /></div>
                <Button onClick={sendTest} className="btn-outline" data-testid="email-test-send"><Send className="h-4 w-4" /> Send Test</Button>
              </div>
              <Button onClick={saveEmail} className="btn-dark" data-testid="save-email-settings"><Save className="h-4 w-4" /> Save Email Settings</Button>
            </div>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
}

async function testHook(url, action, reload) {
  try { const { data } = await api.post("/webhooks/test", { url, action_type: action }); data.status === "success" ? toast.success("Test webhook sent") : toast.error(`Test failed: ${data.error || data.response_code}`); reload(); }
  catch (e) { toast.error(apiError(e.response?.data?.detail)); }
}
async function retryHook(id, reload) {
  try { const { data } = await api.post(`/webhooks/retry/${id}`); data.status === "success" ? toast.success("Retry succeeded") : toast.error("Retry failed"); reload(); }
  catch (e) { toast.error(apiError(e.response?.data?.detail)); }
}
